import type { TaskStatus } from '../types';

/**
 * Role-based status compatibility for task actions.
 * Based on ACTIONS.md specification and IS-001/CR-002 adjustment rules.
 */

export type MatterMembershipRole = 'lead' | 'partner' | 'staff';

/**
 * Resolve the caller's effective role on a matter from its team lists.
 * Priority: partner > lead > staff (mirrors MatterDetailPage).
 * Returns undefined when the matter team lists are not in the payload, or the
 * user is not a member — caller should treat that as "no matter authority".
 */
export function getMatterMembershipRole(
  matter:
    | {
        responsibleAttorneys?: { id: number }[] | undefined;
        partners?: { id: number }[] | undefined;
        staffs?: { id: number }[] | undefined;
      }
    | undefined,
  userId: number | undefined,
): MatterMembershipRole | undefined {
  if (!matter || userId == null) return undefined;
  if (matter.partners?.some((p) => p.id === userId)) return 'partner';
  if (matter.responsibleAttorneys?.some((a) => a.id === userId)) return 'lead';
  if (matter.staffs?.some((s) => s.id === userId)) return 'staff';
  return undefined;
}

// Statuses in which the task's ASSIGNEE may log time (Start/Stop), keyed by the
// caller's GLOBAL role. Used only as a fallback when the matter team is not in
// the payload (matter-membership keying below is the primary gate). Cumulative
// by seniority — a higher global role can sit as a lower matter role (e.g. a
// global LEAD who is `staff` on a matter), so it must also cover the lower
// stages where its assigned task can live (incl. LEAD_REJECT rework).
const ACTIVITY_STATUSES: Record<string, TaskStatus[]> = {
  STAFF: ['NEW', 'LEAD_REJECT'],
  LEAD: ['NEW', 'LEAD_REJECT', 'LEAD_PENDING', 'PARTNER_REJECT'],
  PARTNER: ['NEW', 'LEAD_REJECT', 'LEAD_PENDING', 'PARTNER_REJECT', 'PARTNER_PENDING', 'BILLING_REJECT'],
  MD: ['NEW', 'LEAD_REJECT', 'LEAD_PENDING', 'PARTNER_REJECT', 'PARTNER_PENDING', 'BILLING_REJECT', 'BILLING_PENDING'],
};

// Start/Stop gated by MATTER-MEMBERSHIP role (the caller's role on THIS matter),
// not their global role. A global LEAD who is only `staff` on the matter works
// the task as staff — so they must see Start/Stop on their own NEW / LEAD_REJECT
// task (the rework status), which the global-LEAD set alone used to miss.
// Cumulative by seniority for the same reason as above.
const ACTIVITY_STATUSES_BY_MATTER_ROLE: Record<MatterMembershipRole, TaskStatus[]> = {
  staff: ['NEW', 'LEAD_REJECT'],
  lead: ['NEW', 'LEAD_REJECT', 'LEAD_PENDING', 'PARTNER_REJECT'],
  partner: ['NEW', 'LEAD_REJECT', 'LEAD_PENDING', 'PARTNER_REJECT', 'PARTNER_PENDING', 'BILLING_REJECT'],
};

const REVIEW_STATUSES: Record<string, TaskStatus[]> = {
  STAFF: ['NEW', 'LEAD_REJECT'],
  LEAD: ['NEW', 'LEAD_PENDING', 'PARTNER_REJECT'],
  PARTNER: ['PARTNER_PENDING', 'BILLING_REJECT'],
};

// Send-to-Review (forward) gated by MATTER-MEMBERSHIP role, mirroring the
// server's `changeStatus` allowed-transition rules exactly:
//   - staff   → forwards NEW / LEAD_REJECT      → LEAD_PENDING
//   - lead    → forwards LEAD_PENDING / PARTNER_REJECT → PARTNER_PENDING
//   - partner → forwards PARTNER_PENDING / BILLING_REJECT → BILLING_PENDING
// A LEAD-global user who is only `staff` on this matter therefore does NOT see
// "Send to Partner" on a LEAD_PENDING task (the server would reject it anyway).
const REVIEW_BY_MATTER_ROLE: Record<MatterMembershipRole, TaskStatus[]> = {
  staff: ['NEW', 'LEAD_REJECT'],
  lead: ['LEAD_PENDING', 'PARTNER_REJECT'],
  partner: ['PARTNER_PENDING', 'BILLING_REJECT'],
};

// Reject is gated by MATTER-MEMBERSHIP role (the caller's role on THIS matter),
// not their global role. A LEAD-global user who is only `staff` on this matter
// is not the reviewer here and must not reject. MD never rejects (MD only
// accepts at BILLING_PENDING).
const REJECT_BY_MATTER_ROLE: Record<MatterMembershipRole, TaskStatus[]> = {
  lead: ['LEAD_PENDING'],
  partner: ['PARTNER_PENDING'],
  staff: [],
};

/**
 * Reason why the "Finish adjustment" button must stay disabled, expressed as an
 * i18n key (the actual text lives in the locale files), or undefined when finishing
 * is allowed. An adjustment can only be finished once it has at least one logged
 * (non-REJECT) activity AND no timer is still running on it:
 *   - no logged activity yet              → 'adjustment.noActivityToFinish'
 *   - an activity is still running (open)  → 'adjustment.stopActivityToFinish'
 * A running activity is one with no `lastEndTime` (timer not stopped yet).
 */
export function adjustmentFinishBlockKey(
  activities:
    | { status?: string | undefined; lastEndTime?: string | undefined }[]
    | undefined,
): string | undefined {
  const live = (activities ?? []).filter((a) => a.status !== 'REJECT');
  if (live.length === 0) return 'adjustment.noActivityToFinish';
  if (live.some((a) => a.lastEndTime == null)) return 'adjustment.stopActivityToFinish';
  return undefined;
}

/**
 * Whether the current user may reassign a task (CR-006 / IS-004).
 *
 * Gate (matter-membership keyed; MD is a global override):
 *   - MD (global)            → any non-terminal task on any matter
 *   - Partner (matter)       → any non-terminal task on the matter
 *   - Lead (matter)          → only when the task's CURRENT assignee is a lead or staff
 *   - Staff / not on matter  → never
 *
 * This only decides whether to surface the action. The target restriction
 * (lead → staff only) is enforced by the dialog's target list and the server.
 *
 * @param globalRoleType            Caller's global role (from useAuth).
 * @param status                    Task's current newStatus.
 * @param callerMatterRole          Caller's role on THIS matter (getMatterMembershipRole).
 * @param currentAssigneeMatterRole The current assignee's role on THIS matter.
 */
export function canReassign(
  globalRoleType: string | undefined,
  status: TaskStatus,
  callerMatterRole: MatterMembershipRole | undefined,
  currentAssigneeMatterRole: MatterMembershipRole | undefined,
): boolean {
  if (!globalRoleType) return false;
  // Terminal tasks are never reassignable (callers also gate on isTerminalStatus).
  if (status === 'DONE' || status === 'BILLED') return false;
  if (globalRoleType === 'MD') return true;
  if (callerMatterRole === 'partner') return true;
  if (callerMatterRole === 'lead') {
    return (
      currentAssigneeMatterRole === 'lead' ||
      currentAssigneeMatterRole === 'staff'
    );
  }
  return false;
}

/**
 * Whether a task currently has a running timer — a non-REJECT activity with no
 * `lastEndTime` (timer not stopped). Reassigning deletes such activities, so the
 * UI warns first.
 */
export function hasRunningActivity(
  activities:
    | { status?: string | undefined; lastEndTime?: string | null | undefined }[]
    | undefined,
): boolean {
  return (activities ?? []).some(
    (a) => a?.status !== 'REJECT' && a?.lastEndTime == null,
  );
}

/**
 * Whether the task's assignee may log time (Start/Stop) at this status.
 *
 * Primary gate is the caller's MATTER-MEMBERSHIP role (mirrors canReject /
 * canSendToReview), so a global LEAD who is only `staff` on the matter is
 * treated as staff and can still work their own NEW / LEAD_REJECT task. MD
 * (global) is not a matter role, so it falls through to its global set. When
 * the matter team isn't in the payload, fall back to the global role
 * optimistically — the server enforces assignment/membership regardless.
 *
 * @param globalRoleType        Caller's global role (from useAuth).
 * @param status                Task's current newStatus.
 * @param matterMembershipRole  Caller's role on THIS matter, or undefined.
 */
export function canStartStopActivity(
  globalRoleType: string | undefined,
  status: TaskStatus,
  matterMembershipRole?: MatterMembershipRole,
): boolean {
  if (!globalRoleType) return false;
  // MD (global) oversees every matter and isn't a matter-team role — use its set.
  if (globalRoleType === 'MD') return ACTIVITY_STATUSES.MD?.includes(status) ?? false;
  if (matterMembershipRole !== undefined) {
    return ACTIVITY_STATUSES_BY_MATTER_ROLE[matterMembershipRole]?.includes(status) ?? false;
  }
  return ACTIVITY_STATUSES[globalRoleType]?.includes(status) ?? false;
}

/**
 * Whether the caller may send a task onward for review (forward).
 *
 * When the caller's role ON THIS MATTER is known, gate by it (mirrors the
 * server's matter-team enforcement) so a global LEAD who is only `staff` here
 * can't forward a LEAD_PENDING task to the partner. When matter membership is
 * not provided (matter team not in the payload), fall back to the global role
 * optimistically — the server always enforces the real check.
 *
 * @param globalRoleType        Caller's global role (from useAuth).
 * @param status                Task's current newStatus.
 * @param matterMembershipRole  Caller's role on THIS matter (resolved via
 *                              getMatterMembershipRole), or undefined.
 */
export function canSendToReview(
  globalRoleType: string | undefined,
  status: TaskStatus,
  matterMembershipRole?: MatterMembershipRole,
): boolean {
  if (!globalRoleType) return false;
  if (matterMembershipRole !== undefined) {
    return REVIEW_BY_MATTER_ROLE[matterMembershipRole]?.includes(status) ?? false;
  }
  return REVIEW_STATUSES[globalRoleType]?.includes(status) ?? false;
}

/**
 * Whether the caller may reject a task at this status.
 *
 * Authorization follows the caller's MATTER-MEMBERSHIP role for lead/partner
 * scenarios; MD (global) bypasses matter membership for billing-stage rejects:
 *   - matter-lead    → may reject LEAD_PENDING
 *   - matter-partner → may reject PARTNER_PENDING
 *   - matter-staff   → never
 *   - not on matter  → never (unless MD)
 *   - MD (global)    → may reject BILLING_PENDING (sends partner-approved work back)
 *
 * @param globalRoleType        Caller's global role (from useAuth).
 * @param status                Task's current newStatus.
 * @param matterMembershipRole  Caller's role on THIS matter (resolved via
 *                              getMatterMembershipRole). Undefined → deny
 *                              unless the caller is global MD.
 */
export function canReject(
  globalRoleType: string | undefined,
  status: TaskStatus,
  matterMembershipRole?: MatterMembershipRole,
): boolean {
  if (!globalRoleType) return false;
  // MD reviews everything that hits BILLING_PENDING — no matter-team gate.
  if (globalRoleType === 'MD') return status === 'BILLING_PENDING';
  if (matterMembershipRole === undefined) return false;
  return REJECT_BY_MATTER_ROLE[matterMembershipRole]?.includes(status) ?? false;
}

/**
 * Whether the current user may create an adjustment on an original task.
 *
 * Authorization follows the user's MATTER-MEMBERSHIP role (IS-001/CR-002):
 *   - MD (global)            → any status
 *   - Lead (resp. attorney)  → LEAD_PENDING or PARTNER_REJECT
 *   - Partner (matter partner) → PARTNER_PENDING or BILLING_REJECT
 *   - Staff                  → never
 *
 * @param globalRoleType      The user's global role (from useAuth)
 * @param status              The original task's current newStatus
 * @param matterMembershipRole  The user's effective role on THIS matter:
 *                            'lead' | 'partner' | 'staff' | undefined.
 *                            Pass undefined when the task payload does not
 *                            include matter team data — the function will fall
 *                            back to globalRoleType for an optimistic gate
 *                            (server always enforces the real check).
 */
export function canCreateAdjustment(
  globalRoleType: string | undefined,
  status: TaskStatus,
  matterMembershipRole?: 'lead' | 'partner' | 'staff',
): boolean {
  if (!globalRoleType) return false;

  // MD may always create an adjustment regardless of matter membership
  if (globalRoleType === 'MD') return true;

  // Resolve effective matter role. When membership is not provided, fall back
  // to the global role as an optimistic indicator (server enforces the real check).
  const effectiveRole: string = matterMembershipRole !== undefined ? matterMembershipRole : globalRoleType.toLowerCase();

  if (effectiveRole === 'lead') {
    return status === 'LEAD_PENDING' || status === 'PARTNER_REJECT';
  }
  if (effectiveRole === 'partner') {
    return status === 'PARTNER_PENDING' || status === 'BILLING_REJECT';
  }
  // staff (or any unrecognized role) → never
  return false;
}
